import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PiArrowLeftLight, PiCaretRightLight } from 'react-icons/pi';

/**
 * The chrome an admin DETAIL wears when it renders inside the settings shell.
 *
 * A detail opened from a settings section is a sub-view of that section, not a
 * page of its own: the rail stays, the shell keeps drawing the section header,
 * and this supplies the one thing the shell cannot know — which record you are
 * on, and the way back to the list.
 *
 * Standalone, these details use `DetailFrame` (its own header bar, its own
 * breadcrumbs). Inside the shell that would be a second page inside a page,
 * which is why the detail components take an `embedded` flag and swap this in.
 */

export interface AdminDetailHeaderProps {
  /** Where the list lives, e.g. `/settings/admin-users`. */
  backTo: string;
  /** Already-translated label of the section, e.g. "Users". */
  sectionLabel: string;
  /** Already-translated name of the record. Falsy renders nothing (loading). */
  recordLabel?: string;
  /** Buttons for this record (Edit, Assign role…). */
  actions?: React.ReactNode;
}

const AdminDetailHeader: React.FC<AdminDetailHeaderProps> = ({
  backTo,
  sectionLabel,
  recordLabel,
  actions,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-hairline-soft pb-3">
      <nav aria-label={t('Breadcrumb')} className="flex min-w-0 items-center gap-1.5 text-xs">
        <Link
          to={backTo}
          className="flex flex-shrink-0 items-center gap-1 text-fg-muted hover:text-fg-body"
        >
          <PiArrowLeftLight className="h-3.5 w-3.5" />
          {sectionLabel}
        </Link>
        {recordLabel && (
          <>
            <PiCaretRightLight aria-hidden className="h-3 w-3 flex-shrink-0 text-fg-muted/60" />
            <span className="truncate font-medium text-fg-strong" title={recordLabel}>
              {recordLabel}
            </span>
          </>
        )}
      </nav>
      {actions && <div className="flex flex-shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
};

export default AdminDetailHeader;
